import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi, portalApi } from '../services/portalApi';
import { queryClient } from '../queries/queryClient';
import { unsubscribeFromPush } from '../pwa/push';
import { previewAs } from '../utils/roles';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [presets, setPresets] = useState({});
  const [previewRole, setPreviewRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  const loadPermissions = useCallback(async () => {
    try {
      const me = await portalApi.me();
      setPermissions(me.user?.permissions || []);
    } catch (_) {
      setPermissions([]);
    }
  }, []);

  const loadPresets = useCallback(async () => {
    try {
      const res = await portalApi.teamPermissions();
      setPresets(res?.presets || {});
    } catch (_) {
      setPresets({});
    }
  }, []);

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    setOffline(false);
    setLoading(true);
    for (let attempt = 0; ; attempt += 1) {
      try {
        const { user: profile } = await authApi.profile();
        setUser(profile);
        await loadPermissions();
        if (profile?.userType === 'platform_admin') await loadPresets();
        break;
      } catch (err) {
        if ([401, 403].includes(err.response?.status)) {
          localStorage.removeItem('token');
          setUser(null);
          break;
        }
        if (attempt === 3) {
          setOffline(true);
          break;
        }
        await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
      }
    }
    setLoading(false);
  }, [loadPermissions, loadPresets]);

  useEffect(() => {
    loadProfile();
    const onUnauthorized = () => {
      queryClient.clear();
      setUser(null);
    };
    window.addEventListener('unauthorized', onUnauthorized);
    return () => window.removeEventListener('unauthorized', onUnauthorized);
  }, [loadProfile]);

  const login = async (email, password) => {
    localStorage.removeItem('impersonateOrg');
    queryClient.clear();
    const { token, user: profile } = await authApi.login(email, password);
    localStorage.setItem('token', token);
    setUser(profile);
    await loadPermissions();
    return profile;
  };

  const logout = async () => {
    const token = localStorage.getItem('token');
    try {
      await Promise.race([
        unsubscribeFromPush({ authToken: token }),
        new Promise((resolve) => setTimeout(resolve, 2000))
      ]);
    } catch (_) {
      /* best effort, including offline logout */
    }
    try {
      await authApi.logout();
    } catch (_) {
      /* ignore */
    }
    localStorage.removeItem('token');
    localStorage.removeItem('impersonateOrg');
    queryClient.clear();
    setUser(null);
    setPermissions([]);
    setPresets({});
    setPreviewRole(null);
  };

  const clearPasswordReset = () => setUser((u) => (u ? { ...u, requirePasswordReset: false } : u));
  const updateUser = useCallback((profile) => setUser((u) => (u ? { ...u, ...profile } : u)), []);

  const isPlatformAdmin = user?.userType === 'platform_admin';
  const { user: effUser, permissions: effPerms } = previewAs(user, permissions, previewRole, presets);
  const can = useCallback((perm) => effPerms.includes(perm), [effPerms]);

  return (
    <AuthContext.Provider
      value={{
        user: effUser,
        permissions: effPerms,
        can,
        isPlatformAdmin,
        previewRole,
        setPreviewRole,
        loading,
        offline,
        retry: loadProfile,
        login,
        logout,
        clearPasswordReset,
        updateUser,
        isAuthenticated: !!user,
        mustResetPassword: !!user?.requirePasswordReset
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
